import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Plus,
  Search,
  Printer,
  Edit,
  Check,
  Copy,
  FileDown,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { showPostSuccess, showPostError } from '@/lib/swal'

import { PageHeader } from '@/components/shared/PageHeader'
import { EmptyState } from '@/components/shared/EmptyState'
import { ListLoader } from '@/components/shared/ListLoader'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useAuthStore } from '@/store/auth.store'
import { billsService } from '@/services/billsService'
import { BillListItem, BillVendor } from '@/api/types'
import { formatCalendarDate } from '@/lib/calendarDate'
import { SortOrderToggle } from '@/components/shared/SortOrderToggle'
import type { ListSortDir } from '@/lib/listSort'
import { formatCurrency } from '@/lib/format'
import { formatOverduePayStatusLabel, getDocumentPayStatus, type DocumentPayStatus } from '@/lib/payStatus'

export const BillsList: React.FC = () => {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const { user } = useAuthStore()

  const [bills, setBills] = useState<BillListItem[]>([])
  const [vendors, setVendors] = useState<BillVendor[]>([])
  const [loading, setLoading] = useState(false)
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [totalCount, setTotalCount] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [searchQuery, setSearchQuery] = useState('')
  const [sortDir, setSortDir] = useState<ListSortDir>('desc')
  const [selectedVendorId, setSelectedVendorId] = useState<number | undefined>(undefined)
  const [billDateFrom, setBillDateFrom] = useState<string>('')
  const [billDateTo, setBillDateTo] = useState<string>('')
  const [postingBillId, setPostingBillId] = useState<number | null>(null)

  const branchId = user?.selectedBranch?.branchId

  // Load vendors for filter
  useEffect(() => {
    if (!branchId) return
    const loadVendors = async () => {
      try {
        const vendorList = await billsService.getAllVendors(branchId)
        setVendors(vendorList)
      } catch (error: any) {
        // Error handled silently
      }
    }
    loadVendors()
  }, [branchId])

  // Load bills
  useEffect(() => {
    if (!branchId) {
      return
    }

    const loadBills = async () => {
      try {
        setLoading(true)
        const response = await billsService.getBills(branchId, pageNumber, pageSize, sortDir)
        const list = response.data || []
        setBills(list)
        setTotalCount(response.totalRecords || list.length || 0)
        const pages = response.totalRecords ? Math.ceil(response.totalRecords / response.pageSize) : 0
        setTotalPages(pages || 1)
      } catch (error: any) {
        const status = error.response?.status
        const msg = (error.response?.data?.message || '').toLowerCase()
        const isNoData = status === 404 || /not found|no data|no records/.test(msg)
        setBills([])
        setTotalCount(0)
        setTotalPages(1)
        if (!isNoData) {
          toast.error(error.response?.data?.message || t('bills.failedToLoadBills'))
        }
      } finally {
        setLoading(false)
      }
    }

    loadBills()
  }, [branchId, pageNumber, pageSize, sortDir])

  const filteredBills = useMemo(() => {
    return bills.filter((bill) => {
      if (selectedVendorId && bill.vendorId !== selectedVendorId) {
        return false
      }

      if (billDateFrom && bill.billDate && bill.billDate < billDateFrom) {
        return false
      }

      if (billDateTo && bill.billDate && bill.billDate > billDateTo) {
        return false
      }

      if (!searchQuery.trim()) return true
      const query = searchQuery.toLowerCase()
      const vendorName = (bill as any).vendorNameEn || (bill as any).vendorNameAr || bill.vendorName || ''
      return (
        (bill.billNo || bill.billNumber || '').toLowerCase().includes(query) ||
        vendorName.toLowerCase().includes(query)
      )
    })
  }, [bills, selectedVendorId, searchQuery, billDateFrom, billDateTo])

  const formatDate = (dateString?: string) => formatCalendarDate(dateString)

  const formatAmount = (amount?: number) => {
    if (amount === undefined || amount === null) return '0.00'
    return formatCurrency(Number(amount))
  }

  const payStatusLabel = (status: DocumentPayStatus, dueDate?: string) => {
    if (status === 'paid') return t('bills.paid')
    if (status === 'overdue') {
      return formatOverduePayStatusLabel(
        dueDate,
        t,
        'bills.overdue',
        'bills.overdueWithDuration',
      )
    }
    return t('bills.notPaid')
  }

  const payStatusTextClass = (status: DocumentPayStatus) => {
    if (status === 'paid') return 'text-green-600 dark:text-green-500'
    if (status === 'overdue') return 'text-red-600 dark:text-red-500'
    return 'text-amber-600 dark:text-amber-500'
  }

  const handleAddBill = () => {
    navigate('/purchases/bills/new')
  }

  const handleEditBill = (bill: BillListItem) => {
    const billId = bill.billId
    if (!billId) {
      toast.error('Invalid bill ID')
      return
    }
    const isPosted = bill.isPosted === true || bill.isPosted === 1
    if (isPosted) {
      toast.error(t('bills.postedBillCannotUpdate'))
      return
    }
    navigate(`/purchases/bills/${billId}/edit`, { state: { bill } })
  }

  const getUserIPAddress = async (): Promise<string> => {
    try {
      const response = await fetch('https://api.ipify.org?format=json')
      const data = await response.json()
      return data.ip || '0.0.0.0'
    } catch {
      return '0.0.0.0'
    }
  }

  const handlePostBill = async (bill: BillListItem) => {
    const billId = bill.billId
    if (!billId) {
      toast.error('Invalid bill ID')
      return
    }
    const isPosted = bill.isPosted === true || bill.isPosted === 1
    if (isPosted) {
      toast.error(t('bills.alreadyPosted'))
      return
    }

    try {
      setPostingBillId(billId)
      const ipAddress = await getUserIPAddress()
      const response = await billsService.postBill(billId, ipAddress)
      const result = response?.result || response
      if (result?.isError) {
        await showPostError(result)
        return
      }
      await showPostSuccess(t('bills.postSuccess'))
      const list = await billsService.getBills(branchId!, pageNumber, pageSize, sortDir)
      setBills(list.data || [])
    } catch (error: any) {
      await showPostError(error)
    } finally {
      setPostingBillId(null)
    }
  }

  const handlePrint = () => {
    toast.success('Print functionality coming soon')
  }

  const handlePrintBill = (billId?: number) => {
    if (!billId) {
      toast.error(t('bills.invalidBillId'))
      return
    }
    navigate(`/purchases/bills/${billId}/print`)
  }

  const handleCopy = () => {
    toast.success('Copy functionality coming soon')
  }

  const handleCsv = () => {
    toast.success('CSV export coming soon')
  }

  const handlePageSizeChange = (value: string) => {
    setPageSize(Number(value))
    setPageNumber(1)
  }

  const handleVendorFilterChange = (value: string) => {
    if (value === 'all') {
      setSelectedVendorId(undefined)
    } else {
      setSelectedVendorId(Number(value))
    }
    setPageNumber(1)
  }

  if (!branchId) {
    return (
      <div className="space-y-6 p-6">
        <PageHeader
          title={t('bills.title')}
          description={t('bills.manageBills')}
        />
        <Card>
          <CardContent className="p-6">
            <EmptyState
              title={t('bills.branchNotSelected')}
              description={t('bills.selectBranchToViewBills')}
            />
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title={t('bills.title')}
        description={t('bills.manageBills')}
        action={{
          label: t('bills.addNew'),
          onClick: handleAddBill,
          icon: Plus,
        }}
      />

      <Card>
        <CardContent className="p-6">
          {/* Filters */}
          <div className="space-y-4 mb-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">{t('bills.billDateFrom')}</label>
                <Input
                  type="date"
                  value={billDateFrom}
                  onChange={(e) => setBillDateFrom(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">{t('bills.billDateTo')}</label>
                <Input
                  type="date"
                  value={billDateTo}
                  onChange={(e) => setBillDateTo(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">{t('bills.vendor')}</label>
                <Select value={selectedVendorId ? String(selectedVendorId) : 'all'} onValueChange={handleVendorFilterChange}>
                  <SelectTrigger>
                    <SelectValue placeholder={t('bills.all')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('bills.all')}</SelectItem>
                    {vendors.map((vendor) => (
                      <SelectItem key={vendor.vendorId || vendor.id} value={String(vendor.vendorId || vendor.id)}>
                        {vendor.vendorName || vendor.vendorNameEn || vendor.vendorNameAr || `Vendor ${vendor.vendorId || vendor.id}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Search and Actions */}
            <div className="flex items-center gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder={t('bills.searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              <div className="flex items-center gap-2">
                <SortOrderToggle
                  value={sortDir}
                  onChange={(next) => {
                    setSortDir(next)
                    setPageNumber(1)
                  }}
                />
                <Button variant="outline" size="sm" onClick={handleCopy}>
                  <Copy className="w-4 h-4 mr-2" />
                  {t('bills.copy')}
                </Button>
                <Button variant="outline" size="sm" onClick={handleCsv}>
                  <FileDown className="w-4 h-4 mr-2" />
                  {t('bills.csv')}
                </Button>
                <Button variant="outline" size="sm" onClick={handlePrint}>
                  <Printer className="w-4 h-4 mr-2" />
                  {t('bills.print')}
                </Button>
              </div>
            </div>
          </div>

          {/* Table */}
          {loading ? (
            <ListLoader message={t('bills.loadingBills')} />
          ) : filteredBills.length === 0 ? (
            <EmptyState
              title={t('bills.noBillsFound')}
              description={t('bills.getStarted')}
              action={{
                label: t('bills.addNewBillAction'),
                onClick: handleAddBill,
              }}
            />
          ) : (
            <>
              <div className="overflow-x-auto border rounded-md">
                <Table>
                  <TableHeader>
                    <TableRow className="border-b-2 border-primary/20 hover:bg-transparent">
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        {t('bills.billNo')}
                      </TableHead>
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        {t('bills.vendor')}
                      </TableHead>
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        {t('bills.date')}
                      </TableHead>
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        {t('bills.dueDate')}
                      </TableHead>
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        {t('bills.amount')}
                      </TableHead>
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        {t('bills.paymentStatus')}
                      </TableHead>
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        {t('bills.posted')}
                      </TableHead>
                      <TableHead className="h-12 px-4 text-center w-[100px] bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        {t('bills.print')}
                      </TableHead>
                      <TableHead className="h-12 px-4 text-center w-[100px] bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        {t('bills.edit')}
                      </TableHead>
                      <TableHead className="h-12 px-4 text-center w-[100px] bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        {t('bills.post')}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredBills.map((bill) => {
                      const isPosted = bill.isPosted === true || bill.isPosted === 1
                      const payStatus = getDocumentPayStatus({
                        payStatus: bill.payStatus,
                        remainingAmount: bill.remainingAmount,
                        paidAmount: bill.paidAmount,
                        total: bill.amount || bill.total,
                        dueDate: bill.dueDate,
                      })
                      return (
                      <TableRow key={bill.billId} className="border-b hover:bg-primary/5 dark:hover:bg-primary/10 transition-colors">
                        <TableCell className="px-4 py-2 text-sm text-center align-middle font-medium">
                          {bill.billNo || bill.billNumber || 'N/A'}
                        </TableCell>
                        <TableCell className="px-4 py-2 text-sm text-center align-middle">
                          {(bill as any).vendorNameEn || (bill as any).vendorNameAr || bill.vendorName || 'N/A'}
                        </TableCell>
                        <TableCell className="px-4 py-2 text-sm text-center align-middle">
                          {formatDate(bill.billDate)}
                        </TableCell>
                        <TableCell className="px-4 py-2 text-sm text-center align-middle">
                          {formatDate(bill.dueDate)}
                        </TableCell>
                        <TableCell className="px-4 py-2 text-sm text-center align-middle">
                          {formatAmount(bill.amount || bill.total)}
                        </TableCell>
                        <TableCell className={`px-4 py-2 text-sm text-center align-middle ${payStatusTextClass(payStatus)}`}>
                          {payStatusLabel(payStatus, bill.dueDate)}
                        </TableCell>
                        <TableCell
                          className={`px-4 py-2 text-sm text-center align-middle ${
                            isPosted ? 'text-blue-800 dark:text-blue-300' : 'text-sky-500 dark:text-sky-400'
                          }`}
                        >
                          {isPosted ? t('bills.posted') : t('bills.notPosted')}
                        </TableCell>
                        <TableCell className="px-4 py-2 text-center align-middle">
                          <div className="flex justify-center">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handlePrintBill(bill.billId)}
                              className="h-7 w-7 p-0 hover:bg-primary/10 dark:hover:bg-primary/20"
                            >
                              <Printer className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                        <TableCell className="px-4 py-2 text-center align-middle">
                          <div className="flex justify-center">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditBill(bill)}
                              disabled={isPosted}
                              className="h-7 w-7 p-0 hover:bg-primary/10 dark:hover:bg-primary/20"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                        <TableCell className="px-4 py-2 text-center align-middle">
                          <div className="flex justify-center">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handlePostBill(bill)}
                              disabled={isPosted || postingBillId === bill.billId}
                              title={isPosted ? t('bills.alreadyPosted') : t('bills.post')}
                              className="h-7 w-7 p-0 hover:bg-primary/10 dark:hover:bg-primary/20"
                            >
                              <Check className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    )})}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between mt-4">
                <div className="text-sm text-muted-foreground">
                  {t('common.showing')} {filteredBills.length > 0 ? 1 : 0} {t('common.to')} {filteredBills.length} {t('common.of')} {totalCount} {t('common.entries')}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPageNumber((prev) => Math.max(1, prev - 1))}
                    disabled={pageNumber === 1}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-sm">
                    {t('common.page')} {pageNumber} {t('common.of')} {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPageNumber((prev) => Math.min(totalPages, prev + 1))}
                    disabled={pageNumber >= totalPages}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                  <Select value={String(pageSize)} onValueChange={handlePageSizeChange}>
                    <SelectTrigger className="w-20">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10">10</SelectItem>
                      <SelectItem value="25">25</SelectItem>
                      <SelectItem value="50">50</SelectItem>
                      <SelectItem value="100">100</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

